import {
  cashRequiredByBorrowerClosingDayAtom,
  cashToSellerOnClosingDayAtom,
} from "@/financeModels/owasco/general";
import {
  Item,
  sourcesAtom,
  sourcesTotalCostAtom,
  usesAtom,
  usesTotalCostAtom,
} from "@/financeModels/owasco/sourcesAndUses";
import { Getter, atom } from "jotai";
import {
  SpreadsheetCellStyle,
  boldStyle,
  createEmptyRow,
  createTitleRow,
  greyHighlightStyle,
} from "./helper";

const XLSXStub = () => import("xlsx-js-style");
type XLSXModule = Awaited<ReturnType<typeof XLSXStub>>;

const currencyCell = (value: unknown, style?: SpreadsheetCellStyle) => {
  const numValue = Number(value);
  const isNumeric = value != null && !isNaN(numValue);
  return {
    v: isNumeric ? Number(numValue.toFixed(2)) : "",
    t: isNumeric ? "n" : "s",
    z: isNumeric ? '"$"#,##0;[Red]("$"#,##0)' : undefined,
    s: style,
  };
};

const stringCell = (value: string, style?: SpreadsheetCellStyle) => ({
  v: value,
  t: "s",
  s: style,
});

const percentCell = (
  part: unknown,
  total: number,
  style?: SpreadsheetCellStyle,
) => {
  const numPart = Number(part);
  if (!total || isNaN(numPart) || part == null)
    return { v: "", t: "s", z: undefined, s: style };
  return {
    v: numPart / total,
    t: "n",
    z: "0.00%",
    s: style,
  };
};

const buildItemRows = (items: Item[], total: number) =>
  items.flatMap((item) => {
    const [label, value, , children] = item;
    const rows: unknown[][] = [
      [stringCell(label), currencyCell(value), percentCell(value, total)],
    ];
    if (children) {
      for (const [childLabel, childValue] of children) {
        rows.push([
          stringCell("   " + childLabel),
          currencyCell(childValue),
          percentCell(childValue, total),
        ]);
      }
    }
    return rows;
  });

export const buildOverviewWorksheet = (get: Getter, XLSX: XLSXModule) => {
  const pageTitle = (window.document.title || "ETA IQ Overview")
    .replace("Model (", "")
    .replace(")", "");

  const cashRequiredByBorrower = get(cashRequiredByBorrowerClosingDayAtom);
  const cashToSeller = get(cashToSellerOnClosingDayAtom);
  const sources = get(sourcesAtom);
  const uses = get(usesAtom);
  const sourcesTotal = get(sourcesTotalCostAtom);
  const usesTotal = get(usesTotalCostAtom);

  const sourceRows = buildItemRows(sources, sourcesTotal);
  const useRows = buildItemRows(uses, usesTotal);

  const maxLen = Math.max(sourceRows.length, useRows.length);
  const sideBySideRows = Array.from({ length: maxLen }, (_, i) => {
    const left = sourceRows[i] ?? [
      stringCell(""),
      stringCell(""),
      stringCell(""),
    ];
    const right = useRows[i] ?? [
      stringCell(""),
      stringCell(""),
      stringCell(""),
    ];
    return [...left, stringCell(""), ...right];
  });

  const rows = [
    [pageTitle],
    ["Created on - " + new Date().toLocaleString()],
    createEmptyRow(),
    createTitleRow("CASH REQUIRED ON CLOSING DAY"),
    createEmptyRow(),
    [
      stringCell("Cash Required by Borrower on Closing Day", boldStyle),
      currencyCell(cashRequiredByBorrower, boldStyle),
    ],
    [
      stringCell("Amount Paid to Seller on Closing Day", boldStyle),
      currencyCell(cashToSeller, boldStyle),
    ],
    createEmptyRow(),
    createTitleRow("SOURCES AND USES"),
    createEmptyRow(),
    [
      stringCell("Sources", boldStyle),
      stringCell("Amount", boldStyle),
      stringCell("% of Total", boldStyle),
      stringCell(""),
      stringCell("Uses", boldStyle),
      stringCell("Amount", boldStyle),
      stringCell("% of Total", boldStyle),
    ],
    createEmptyRow(),
    ...sideBySideRows,
    createEmptyRow(),
    [
      stringCell("Total Sources", greyHighlightStyle),
      currencyCell(sourcesTotal, greyHighlightStyle),
      stringCell("", greyHighlightStyle),
      stringCell(""),
      stringCell("Total Uses", greyHighlightStyle),
      currencyCell(usesTotal, greyHighlightStyle),
      stringCell("", greyHighlightStyle),
    ],
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);

  const COL_WIDTH = 260;
  if (!ws["!cols"]) ws["!cols"] = [];
  if (!ws["!cols"][0]) ws["!cols"][0] = { wch: 8 };
  ws["!cols"][0].wpx = COL_WIDTH;
  // Column E (index 4) is the Uses label column in the side-by-side layout
  if (!ws["!cols"][4]) ws["!cols"][4] = { wch: 8 };
  ws["!cols"][4].wpx = COL_WIDTH;

  return ws;
};

export const overviewExportAtom = atom(null, async (get) => {
  const XLSX = await XLSXStub();
  const ws = buildOverviewWorksheet(get, XLSX);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Overview");

  const fmt = "xlsx";
  const filename = `overview-${new Date()
    .toISOString()
    .replace(/:/g, "-")
    .replace(/\..+/, "")}.${fmt}`;

  XLSX.writeFile(wb, filename);

  return;
});
